(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const transitionDuration = 80;
  const prefetchLimit = 10;
  let navigating = false;
  let controller = null;
  const pageCache = new Map();
  const loadedExternalScripts = new Set(
    Array.from(document.querySelectorAll('script[src]')).map((script) => new URL(script.src, window.location.href).href)
  );

  const makeOverlay = () => {
    if (document.querySelector('.lp-page-transition')) return;
    const overlay = document.createElement('div');
    overlay.className = 'lp-page-transition';
    overlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(overlay);
  };

  const resetState = () => {
    navigating = false;
    document.body.classList.remove('lp-page-leave');
    document.body.classList.add('lp-page-enter');
  };

  const isModifiedClick = (event) => (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );

  const previewThemeId = () => new URL(window.location.href).searchParams.get('preview_theme_id');

  const withPreviewContext = (url) => {
    const previewId = previewThemeId();
    if (previewId && !url.searchParams.has('preview_theme_id')) {
      url.searchParams.set('preview_theme_id', previewId);
    }
    return url;
  };

  const shouldTransition = (link) => {
    if (
      !link ||
      link.target ||
      link.hasAttribute('download') ||
      link.dataset.noTransition === 'true' ||
      link.closest('[data-shop-link]') ||
      link.closest('[data-care-link]')
    ) return false;
    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return false;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return false;
    if (url.pathname.startsWith('/admin') || url.pathname.startsWith('/apps')) return false;
    if (url.pathname === window.routes?.cart_add_url) return false;

    const samePage = url.pathname === window.location.pathname && url.search === window.location.search;
    if (samePage && url.hash) return false;

    return true;
  };

  const cacheKey = (url) => url.href;

  const trimPageCache = () => {
    while (pageCache.size > prefetchLimit) {
      pageCache.delete(pageCache.keys().next().value);
    }
  };

  const fetchPage = (url, signal) => {
    const key = cacheKey(url);
    const cached = pageCache.get(key);
    if (cached) return cached;

    const request = fetch(url.href, {
      credentials: 'same-origin',
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then((response) => {
      if (!response.ok) throw new Error(`Soft navigation failed: ${response.status}`);
      return response.text();
    });

    pageCache.set(key, request);
    trimPageCache();
    request.catch(() => pageCache.delete(key));
    return request;
  };

  const prefetchPage = (link) => {
    if (!shouldTransition(link)) return;
    const url = withPreviewContext(new URL(link.href, window.location.href));
    if (pageCache.has(cacheKey(url))) return;
    fetchPage(url).catch(() => {});
  };

  const prefetchPrimaryNavigation = () => {
    document.querySelectorAll('.lp-nav__links a[href], .lp-nav__drawer-links a[href]').forEach(prefetchPage);
  };

  const shouldTransitionForm = (form) => {
    if (!form || form.dataset.noTransition === 'true' || form.target) return false;
    const method = (form.getAttribute('method') || 'get').toLowerCase();
    if (method !== 'get') return false;
    const action = form.getAttribute('action') || window.location.href;
    const url = new URL(action, window.location.href);
    if (url.origin !== window.location.origin) return false;
    if (form.closest('[data-lp-collection-section]') && form.matches('.lp-shop-sort')) return false;
    return true;
  };

  const formUrl = (form, submitter) => {
    const url = withPreviewContext(new URL(form.getAttribute('action') || window.location.href, window.location.href));
    const params = new URLSearchParams(new FormData(form));
    if (submitter?.name) params.set(submitter.name, submitter.value || '');
    url.search = params.toString();
    return url;
  };

  const runScripts = (container) => {
    container.querySelectorAll('script[src]').forEach((script) => {
      const src = new URL(script.src, window.location.href).href;
      if (loadedExternalScripts.has(src)) {
        script.remove();
        return;
      }

      const nextScript = document.createElement('script');
      Array.from(script.attributes).forEach((attribute) => {
        nextScript.setAttribute(attribute.name, attribute.value);
      });
      nextScript.async = false;
      loadedExternalScripts.add(src);
      script.replaceWith(nextScript);
    });

    container.querySelectorAll('script:not([src])').forEach((script) => {
      const type = script.getAttribute('type');
      if (type && !['text/javascript', 'application/javascript', 'module'].includes(type)) return;
      const nextScript = document.createElement('script');
      Array.from(script.attributes).forEach((attribute) => {
        nextScript.setAttribute(attribute.name, attribute.value);
      });
      nextScript.textContent = script.textContent;
      script.replaceWith(nextScript);
    });
  };

  const updateHead = (doc) => {
    if (doc.title) document.title = doc.title;
    const nextCanonical = doc.querySelector('link[rel="canonical"]');
    const currentCanonical = document.querySelector('link[rel="canonical"]');
    if (nextCanonical && currentCanonical) currentCanonical.href = nextCanonical.href;
  };

  const updateHeaderActiveState = (url) => {
    const links = document.querySelectorAll('.lp-nav__links a, .lp-nav__drawer-links a');
    links.forEach((link) => {
      const linkUrl = new URL(link.href, window.location.href);
      const linkPath = linkUrl.pathname.replace(/\/$/, '') || '/';
      const currentPath = url.pathname.replace(/\/$/, '') || '/';
      let isActive = false;
      if (currentPath === '/' && linkPath === '/') {
        isActive = true;
      } else if (linkPath.startsWith('/collections')) {
        isActive =
          currentPath.startsWith('/collections') ||
          currentPath.startsWith('/products') ||
          currentPath === '/becoming';
      } else if (linkPath !== '/') {
        isActive = currentPath === linkPath || currentPath.startsWith(`${linkPath}/`);
      }
      link.classList.toggle('is-active', isActive);
    });
  };

  const scrollToNextPosition = (url) => {
    if (url.hash) {
      const target = document.getElementById(url.hash.slice(1));
      if (target) {
        target.scrollIntoView({ block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  const replaceMain = async (url, pushState = true) => {
    const main = document.getElementById('MainContent');
    if (!main) {
      window.location.href = url.href;
      return;
    }

    if (controller) controller.abort();
    controller = new AbortController();
    navigating = true;

    try {
      const html = await fetchPage(url, controller.signal);
      document.body.classList.remove('lp-page-enter');
      document.body.classList.add('lp-page-leave');
      if (!prefersReducedMotion) {
        await new Promise((resolve) => window.setTimeout(resolve, transitionDuration));
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextMain = doc.getElementById('MainContent');
      if (!nextMain) throw new Error('Soft navigation target missing');

      updateHead(doc);
      main.innerHTML = nextMain.innerHTML;
      runScripts(main);
      window.limpideBindComponents?.(main);
      updateHeaderActiveState(url);

      if (pushState) window.history.pushState({ limpideSoftNavigation: true }, '', url.href);
      scrollToNextPosition(url);
      document.dispatchEvent(new CustomEvent('limpide:page-load', { detail: { url, root: main } }));
      main.focus({ preventScroll: true });
      window.requestAnimationFrame(resetState);
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = url.href;
    } finally {
      controller = null;
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    makeOverlay();
    window.requestAnimationFrame(resetState);

    document.addEventListener('click', (event) => {
      if (navigating || isModifiedClick(event)) return;

      const link = event.target.closest('a[href]');
      if (!shouldTransition(link)) return;

      event.preventDefault();
      replaceMain(withPreviewContext(new URL(link.href, window.location.href)));
    }, true);

    document.addEventListener('pointerenter', (event) => {
      const link = event.target.closest?.('a[href]');
      if (link) prefetchPage(link);
    }, true);

    document.addEventListener('pointerover', (event) => {
      const link = event.target.closest?.('a[href]');
      if (link) prefetchPage(link);
    }, true);

    document.addEventListener('focusin', (event) => {
      const link = event.target.closest?.('a[href]');
      if (link) prefetchPage(link);
    });

    document.addEventListener('touchstart', (event) => {
      const link = event.target.closest?.('a[href]');
      if (link) prefetchPage(link);
    }, { passive: true, capture: true });

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(prefetchPrimaryNavigation, { timeout: 1800 });
    } else {
      window.setTimeout(prefetchPrimaryNavigation, 700);
    }

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!shouldTransitionForm(form)) return;

      event.preventDefault();
      replaceMain(formUrl(form, event.submitter));
    }, true);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resetState();
  });

  window.addEventListener('popstate', () => {
    if (
      document.querySelector('[data-lp-collection-section]') &&
      window.location.pathname.startsWith('/collections')
    ) return;
    replaceMain(withPreviewContext(new URL(window.location.href)), false);
  });
})();
